import {
  Box,
  Checkbox,
  Flex,
  ScrollArea,
  Text,
  Callout,
} from "@radix-ui/themes";
import {
  Dialog,
  Label,
  Button,
  TextField,
  TextFieldSlot,
} from "../elements.js";
import { Form } from "radix-ui";
import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { CopyButton, CopyIconButton } from "../copy-button.js";
import * as React from "react";
import {
  useListOrganizationApiKeyPermissions,
  ListOrganizationApiKeyPermission,
} from "../../api/endpoint.js";
import { useCreateApiKey } from "../api/api-key.js";
import { useApiKeysContext } from "./api-keys-context.js";
import { Translation } from "../i18n/translation.js";
import { useTranslation } from "../i18n/use-translation.js";

export function CreateApiKeyDialog() {
  const {
    state: { createDialogOpen, createdApiKey },
    dispatch,
  } = useApiKeysContext();

  return (
    <Dialog.Root
      open={createDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          dispatch({ type: "CLOSE_CREATE_DIALOG" });
        }
      }}
    >
      {createdApiKey ? (
        <SaveApiKeyContent apiKey={createdApiKey.value} />
      ) : (
        <CreateApiKeyForm
          // always remount the form when the dialog is opened to reset the validation errors
          key={`${createDialogOpen}`}
        />
      )}
    </Dialog.Root>
  );
}

function CreateApiKeyForm() {
  const { dispatch } = useApiKeysContext();
  const translate = useTranslation();
  const [errors, setErrors] = React.useState<{
    name: boolean;
  }>({ name: false });

  const validate = (formData: FormData) => {
    const name = formData.get("name")?.toString() ?? "";
    const errors = { name: !name.trim().length };
    setErrors(errors);
    const isValid = Object.values(errors).every((error) => error === false);
    return isValid;
  };

  const {
    mutate: createOrganizationApiKey,
    error,
    isPending,
    isSuccess,
  } = useCreateApiKey();
  const permissions = useListOrganizationApiKeyPermissions({ limit: 100 });

  return (
    <Dialog.Content maxWidth="529px">
      <Dialog.Title size="4" weight={"bold"}>
        <Translation
          defaultMessage="Create API key"
          id="eUFRNS"
          description="Dialog title for creating a new API key"
        />
      </Dialog.Title>
      <Form.Root
        onSubmit={(e) => {
          if (isPending) return;
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          const isValid = validate(data);
          if (!isValid) {
            return;
          }

          createOrganizationApiKey(
            {
              data: {
                name: data.get("name")?.toString() ?? "",
                permissions: data.getAll("permission").map(String) || [],
              },
            },
            {
              onSuccess: (data) => {
                dispatch({ type: "SET_CREATED_API_KEY", apiKey: data });
              },
            },
          );
        }}
      >
        <Flex direction="column" align="stretch" gap="4">
          {error && (
            <Callout.Root color="red" role="alert">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Callout.Text>
                <Translation
                  defaultMessage="An error occurred while creating the API key."
                  id="0LyBVd"
                  description="Error message when API key creation fails"
                />
              </Callout.Text>
            </Callout.Root>
          )}
          <Form.Field name="name" asChild>
            <Flex direction="column" align="stretch" gap="2">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="Name"
                    id="v0VmRy"
                    description="Label for API key name input field"
                  />
                </Label>
              </Form.Label>
              <Form.Control asChild>
                <TextField
                  data-1p-ignore
                  autoComplete="off"
                  placeholder={translate({
                    defaultMessage: "A descriptive name for the API key",
                    id: "moyvr4",
                    description: "Placeholder for API key name input",
                  })}
                />
              </Form.Control>
              {errors.name && (
                <Form.Message asChild>
                  <Text size="2" color="red">
                    <Translation
                      defaultMessage="The name is required"
                      id="oF9d2V"
                      description="Validation error when API key name is empty"
                    />
                  </Text>
                </Form.Message>
              )}
            </Flex>
          </Form.Field>
          {permissions.isSuccess && permissions.data.data.length > 0 && (
            <PermissionsField permissions={permissions.data.data} />
          )}
        </Flex>

        <Flex align="center" gap="3" justify="end" mt="5">
          <Dialog.Close>
            <Button variant="secondary" disabled={isPending || isSuccess}>
              <Translation
                defaultMessage="Cancel"
                id="AyVAAW"
                description="Button to cancel API key creation"
              />
            </Button>
          </Dialog.Close>
          <Button type="submit" loading={isPending || isSuccess}>
            <Translation
              defaultMessage="Create API key"
              id="d4BNWL"
              description="Button to submit and create the API key"
            />
          </Button>
        </Flex>
      </Form.Root>
    </Dialog.Content>
  );
}

function SaveApiKeyContent({ apiKey }: { apiKey: string }) {
  return (
    <Dialog.Content maxWidth="529px">
      <Dialog.Title size="4" weight={"bold"}>
        <Translation
          defaultMessage="Save your key"
          id="wFFCij"
          description="Dialog title prompting user to save their API key"
        />
      </Dialog.Title>
      <Dialog.Description>
        <Translation
          defaultMessage="Please save this API key in a secure location. If you lose it, you'll need to generate a new one."
          id="sy+2x7"
          description="Warning message about saving the API key"
        />
      </Dialog.Description>
      <Callout.Root mt="5" mb="4">
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Callout.Text>
          <Translation
            defaultMessage="You won't be able to access the key again. Please copy it now."
            id="EFAK/K"
            description="Callout warning that the key won't be accessible again"
          />
        </Callout.Text>
      </Callout.Root>
      <TextField value={apiKey} readOnly>
        <TextFieldSlot side="right">
          <CopyIconButton value={apiKey} />
        </TextFieldSlot>
      </TextField>
      <Flex align="center" gap="3" justify="end" mt="5">
        <Dialog.Close>
          <CopyButton value={apiKey}>
            <Translation
              defaultMessage="Copy and close"
              id="ViYtvQ"
              description="Button to copy API key and close dialog"
            />
          </CopyButton>
        </Dialog.Close>
      </Flex>
    </Dialog.Content>
  );
}

function Permission({
  checked,
  permission,
  onChange,
}: {
  checked: boolean;
  permission: ListOrganizationApiKeyPermission;
  onChange: (isChecked: boolean) => void;
}) {
  return (
    <label
      htmlFor={permission.slug}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "var(--space-2) var(--space-4)",
        gap: "var(--space-3)",
        borderBottom: "1px solid var(--gray-6)",
        cursor: "pointer",
      }}
    >
      <Checkbox
        checked={checked}
        id={permission.slug}
        name="permission"
        value={permission.slug}
        onCheckedChange={(e) => onChange(e === "indeterminate" ? false : e)}
      />
      <Flex direction="column">
        <Text size="2" weight="bold">
          {permission.name}
        </Text>
        <Text size="2" color="gray">
          {permission.description}
        </Text>
      </Flex>
    </label>
  );
}

function PermissionsField({
  permissions = [],
}: {
  permissions: ListOrganizationApiKeyPermission[];
}) {
  const [selectedPermissions, setSelectedPermissions] = React.useState<
    ListOrganizationApiKeyPermission[]
  >([]);
  const [filter, setFilter] = React.useState("");
  const fieldRef = React.useRef<HTMLDivElement | null>(null);
  const translate = useTranslation();
  const filteredPermissions = React.useMemo(() => {
    return permissions.filter((p) => {
      return (
        !filter.trim().length ||
        p.description?.toLowerCase().match(filter.toLowerCase()) ||
        p.name.toLowerCase().match(filter.toLowerCase()) ||
        p.slug.toLowerCase().match(filter.toLowerCase())
      );
    });
  }, [permissions, filter]);

  React.useEffect(() => {
    const onResize = () => {
      if (fieldRef.current && filteredPermissions.length !== 0) {
        fieldRef.current.style.minHeight = "unset";
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [filteredPermissions.length]);

  return (
    <Form.Field name="permissions" asChild>
      <Flex direction="column" align="stretch" gap="1">
        <Form.Label asChild>
          <Label>
            <Translation
              defaultMessage="Permissions"
              id="6YN1wO"
              description="Label for API key permissions field"
            />
          </Label>
        </Form.Label>
        <Text size="2" mb="2" color="gray">
          <Translation
            defaultMessage="Only enable the minimum permissions required for your use case."
            id="q2VEtG"
            description="Help text for API key permissions selection"
          />
        </Text>
        <Flex
          ref={fieldRef}
          style={{
            borderRadius: "var(--radius-3)",
            borderColor: "var(--gray-6)",
            borderWidth: "1px",
            borderStyle: "solid",
          }}
          direction="column"
          align="stretch"
        >
          <TextField
            name="search"
            value={filter}
            size="2"
            onChange={(e) => {
              if (fieldRef.current) {
                fieldRef.current.style.minHeight =
                  fieldRef.current.clientHeight + 2 + "px";
              }
              setFilter(e.target.value);
            }}
            style={{
              height: "40px",
              borderRadius: "var(--radius-3) var(--radius-3) 0 0",
              boxShadow: "none",
            }}
            placeholder={translate({
              defaultMessage: "Search",
              id: "iyNcly",
              description: "Placeholder for permissions search input",
            })}
          >
            <TextFieldSlot side="left" px="3">
              <MagnifyingGlassIcon aria-hidden="true" height="16" width="16" />
            </TextFieldSlot>
          </TextField>
          {filteredPermissions.length > 0 ? (
            <>
              <ScrollArea
                scrollbars="vertical"
                style={{
                  flex: 1,
                  maxHeight: "calc(50vh - 150px)",
                  border: "1px solid var(--gray-6)",
                  borderWidth: "1px 0 0",
                }}
              >
                {filteredPermissions.map((permission) => (
                  <Permission
                    checked={selectedPermissions.some(
                      (sp) => sp.slug === permission.slug,
                    )}
                    key={permission.slug}
                    permission={permission}
                    onChange={(isChecked) =>
                      setSelectedPermissions((prev) =>
                        isChecked
                          ? [...prev, permission]
                          : prev.filter((sp) => sp.slug !== permission.slug),
                      )
                    }
                  />
                ))}
              </ScrollArea>
              <Box
                px="4"
                py="3"
                mt={"-1px"}
                flexGrow="0"
                style={{
                  borderTop: "0 solid var(--gray-6)",
                  borderWidth: "1px 0 0",
                }}
              >
                <SelectedPermissions
                  total={permissions.length}
                  selected={selectedPermissions.length}
                  onToggle={() =>
                    setSelectedPermissions((prev) =>
                      prev.length === permissions.length ? [] : permissions,
                    )
                  }
                />
              </Box>
            </>
          ) : (
            <Flex
              gap="4"
              pt="6"
              pb="6"
              style={{ borderTop: "1px solid var(--gray-6)" }}
              flexGrow="1"
              justify="center"
              align="center"
              direction="column"
            >
              <MagnifyingGlassIcon
                style={{
                  color: "var(--gray-9)",
                  width: "var(--space-5)",
                  height: "var(--space-5)",
                }}
              />
              <Text size="2" align="center" mb="2" wrap="balance" weight="bold">
                <Translation
                  defaultMessage="No permissions match your search"
                  id="xjqT9f"
                  description="Empty state message when no permissions match search"
                />
              </Text>
              <Button
                variant="secondary"
                size="1"
                onClick={() => setFilter("")}
              >
                <Translation
                  defaultMessage="Clear search"
                  id="YrMs63"
                  description="Button to clear permissions search"
                />
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Form.Field>
  );
}

function SelectedPermissions({
  total,
  selected,
  onToggle,
}: {
  total: number;
  selected: number;
  onToggle: () => void;
}) {
  return (
    <Flex align="center" gap="2">
      <Text size="1" style={{ lineHeight: 1, color: "var(--gray-11)" }}>
        {selected === 0 ? (
          <Translation
            defaultMessage="No permissions selected"
            id="FqU9hv"
            description="Message when no permissions are selected"
          />
        ) : selected === 1 ? (
          <Translation
            defaultMessage="1 permission selected"
            id="VouLgT"
            description="Message showing count of selected permissions"
          />
        ) : (
          <Translation
            defaultMessage="{count} permissions selected"
            id="ZL90DI"
            description="Message showing count of selected permissions"
            values={{ count: selected }}
          />
        )}
      </Text>
      <Text
        size="1"
        onClick={onToggle}
        style={{ cursor: "pointer", color: "var(--accent-10)" }}
      >
        {total === selected ? (
          <Translation
            defaultMessage="Deselect all"
            id="63f7SY"
            description="Button to deselect all permissions"
          />
        ) : (
          <Translation
            defaultMessage="Select all"
            id="onTqlA"
            description="Button to select all permissions"
          />
        )}
      </Text>
    </Flex>
  );
}

export function CreateApiKeyButton() {
  const { dispatch } = useApiKeysContext();
  return (
    <Button
      variant="secondary"
      onClick={() => dispatch({ type: "OPEN_CREATE_DIALOG" })}
    >
      <Translation
        defaultMessage="Create API key"
        id="vq5724"
        description="Button to open create API key dialog"
      />
    </Button>
  );
}
